import { useEffect, useState } from "react";
import {
  CheckCircle2,
  ChevronDown,
  Clipboard,
  Loader2,
  MessageSquare,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  Terminal,
  X,
} from "lucide-react";
import { CommandAction, Mission } from "../../lib/commandCore";
import {
  buildExecutionCard,
  buildExecutionCardPreview,
  cleanAgentOutput,
  findSimilarMissionTitle,
  isWeakMissionTitle,
  missionTitleFromOutput,
} from "../../lib/missionExecutionCard";
import {
  AgentCommandResult,
  AgentPreset,
  AgentProvider,
  api,
  CLAUDE_EFFORTS,
  CODEX_EFFORTS,
} from "../../lib/tauri";
import { useTaskStore } from "../../stores/taskStore";
import { useUiStore } from "../../stores/uiStore";
import { Button } from "../ui/Button";
import { PresetPicker } from "./PresetPicker";
import { todayIso } from "../../lib/laneCore";
import { providerLabel } from "../../lib/ai-pwa";

interface Props {
  open: boolean;
  action: CommandAction | null;
  mission: Mission | null;
  onClose: () => void;
}

const actionMeta: Record<CommandAction, { label: string; next: string }> = {
  A: {
    label: "調査",
    next: "状況確認だけを行い、根拠と次アクションを返す。",
  },
  B: {
    label: "案作成",
    next: "実行前の案、比較、リスク、推奨GOを作る。",
  },
  C: {
    label: "実行準備",
    next: "実行に必要な手順と確認事項を整理する。ここでは実行しない。",
  },
  D: {
    label: "保留",
    next: "保留理由、再開条件、次に見るタイミングを残す。",
  },
};

const ownerLabel: Record<Mission["owner"], string> = {
  taiga: "たいがさん",
  codex: providerLabel("codex"),
  claude: providerLabel("claude"),
  grok: "Grok",
  hermes: "Hermes",
};

const agentLabel: Record<AgentProvider, string> = {
  codex: providerLabel("codex"),
  claude: providerLabel("claude"),
};

function buildPrompt(action: CommandAction, mission: Mission | null): string {
  const meta = actionMeta[action];
  if (!mission || mission.isFallback) {
    return `Flow Command Centerで今日のPrimary Mission案を作ってください。\nGO: ${action} ${meta.label}\n制約: 外部書き込み・自動実行なし。根拠とA/B/C/Dを短く出す。`;
  }
  return `Flow Command Centerからの指令です。\n\nGO: ${action} ${meta.label}\nMission: ${mission.title}\n推奨委任先: ${ownerLabel[mission.owner]}\n理由: ${mission.reason}\n期待インパクト: ${mission.impact}\n放置リスク: ${mission.risk}\n\n制約:\n- 外部書き込みなし\n- 自動実行なし\n- 本番送信なし\n- 認証情報の表示なし\n\n次にやること: ${meta.next}`;
}

function buildReplyPrompt(
  action: CommandAction,
  mission: Mission | null,
  agentResult: AgentCommandResult | null,
  selectedReply: CommandAction | null,
  replyDraft: string
): string {
  const basePrompt = buildPrompt(action, mission);
  const replyParts: string[] = [];
  if (selectedReply) {
    replyParts.push(`たいがさんのGO選択: ${selectedReply} ${actionMeta[selectedReply].label}`);
  }
  if (replyDraft.trim()) {
    replyParts.push(`たいがさんの追加入力:\n${replyDraft.trim()}`);
  }
  if (agentResult?.output) {
    replyParts.push(`直前のAgent Result:\n${cleanAgentOutput(agentResult.output)}`);
  }

  return `${basePrompt}\n\n---\n続投指示:\n${replyParts.join(
    "\n\n"
  )}\n\n上記を踏まえて、次の1手を短く具体化してください。危険作業・本番送信・外部書き込みが必要なら実行せず、A/B/C/Dで確認してください。`;
}

export function CommandSheet({ open, action, mission, onClose }: Props) {
  const createTask = useTaskStore((state) => state.createTask);
  const allTasks = useTaskStore((state) => state.allTasks);
  const showToast = useUiStore((state) => state.showToast);
  const [runningAgent, setRunningAgent] = useState<AgentProvider | null>(null);
  const [agentResult, setAgentResult] = useState<AgentCommandResult | null>(null);
  const [agentError, setAgentError] = useState<string | null>(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [showGeneratedCommand, setShowGeneratedCommand] = useState(false);
  const [selectedReply, setSelectedReply] = useState<CommandAction | null>(null);
  const [replyDraft, setReplyDraft] = useState("");
  const [savingMission, setSavingMission] = useState(false);
  const [missionSaveState, setMissionSaveState] = useState<"idle" | "saved" | "error">("idle");
  const [missionSaveError, setMissionSaveError] = useState<string | null>(null);
  const [boardPreset, setBoardPreset] = useState<AgentPreset>("normal");
  const [boardModelOverride, setBoardModelOverride] = useState("");
  const [boardEffortOverride, setBoardEffortOverride] = useState("");
  const [boardProvider, setBoardProvider] = useState<AgentProvider>("codex");

  useEffect(() => {
    if (!open) {
      setRunningAgent(null);
      setAgentResult(null);
      setAgentError(null);
      setShowDiagnostics(false);
      setShowGeneratedCommand(false);
      setSelectedReply(null);
      setReplyDraft("");
      setSavingMission(false);
      setMissionSaveState("idle");
      setMissionSaveError(null);
      // m1: プリセット系もクローズ時に既定（普通）へ戻す
      setBoardPreset("normal");
      setBoardModelOverride("");
      setBoardEffortOverride("");
      setBoardProvider("codex");
    }
  }, [open, action, mission?.id]);

  if (!open || !action) return null;
  const meta = actionMeta[action];
  const prompt = buildPrompt(action, mission);
  const hasReply = selectedReply !== null || replyDraft.trim().length > 0;
  const replyPrompt = hasReply ? buildReplyPrompt(action, mission, agentResult, selectedReply, replyDraft) : prompt;
  const cleanedAgentOutput = agentResult ? cleanAgentOutput(agentResult.output) : "";
  const missionTitlePreview = cleanedAgentOutput ? missionTitleFromOutput(cleanedAgentOutput) : "";
  const isWeakMissionTitlePreview = isWeakMissionTitle(
    missionTitlePreview
  );
  const canSaveMission = Boolean(cleanedAgentOutput) && missionSaveState !== "saved" && !isWeakMissionTitlePreview;
  const executionPreview = cleanedAgentOutput
    ? buildExecutionCardPreview(cleanedAgentOutput, selectedReply)
    : null;
  const today = todayIso();
  const similarMission = missionTitlePreview
    ? findSimilarMissionTitle(missionTitlePreview, allTasks, today)
    : null;
  const blocksDuplicateMission = Boolean(similarMission?.exact);

  const copyPrompt = async () => {
    await navigator.clipboard?.writeText(replyPrompt);
  };

  const copyAgentOutput = async () => {
    if (!cleanedAgentOutput) return;
    await navigator.clipboard?.writeText(cleanedAgentOutput);
  };

  const saveMissionFromResult = async () => {
    if (!agentResult || !cleanedAgentOutput || blocksDuplicateMission) return;
    setSavingMission(true);
    setMissionSaveState("idle");
    setMissionSaveError(null);
    try {
      await createTask({
        title: missionTitleFromOutput(cleanedAgentOutput),
        description: buildExecutionCard(cleanedAgentOutput, selectedReply),
        priority: selectedReply === "C" ? "high" : "medium",
        dueDate: todayIso(),
      });
      setMissionSaveState("saved");
    } catch (error) {
      setMissionSaveState("error");
      setMissionSaveError(error instanceof Error ? error.message : String(error));
    } finally {
      setSavingMission(false);
    }
  };

  const runAgent = async (provider: AgentProvider, mode: "initial" | "reply" = "initial") => {
    setRunningAgent(provider);
    setAgentResult(null);
    setAgentError(null);
    setShowDiagnostics(false);
    setMissionSaveState("idle");
    setMissionSaveError(null);
    try {
      // m2: 直接実行・続投にもプリセットを通す。override は押した provider と
      // 不一致になり得る（picker は boardProvider 追従）ため、不一致分は落として400を防ぐ
      const validEfforts: readonly string[] = provider === "claude" ? CLAUDE_EFFORTS : CODEX_EFFORTS;
      const result = await api.runAgentCommand?.({
        provider,
        prompt: mode === "reply" ? replyPrompt : prompt,
        action: selectedReply ?? action,
        missionId: mission?.id ?? null,
        missionTitle: mission?.title ?? null,
        preset: boardPreset !== "normal" ? boardPreset : null,
        modelOverride: provider === "claude" && boardModelOverride ? boardModelOverride : null,
        effortOverride: validEfforts.includes(boardEffortOverride) ? boardEffortOverride : null,
      });
      if (result) setAgentResult(result);
    } catch (error) {
      setAgentError(error instanceof Error ? error.message : String(error));
    } finally {
      setRunningAgent(null);
    }
  };

  const enqueueToBoard = async (provider: AgentProvider) => {
    setAgentError(null);
    try {
      await api.enqueueAgentJob?.({
        provider,
        prompt: hasReply ? replyPrompt : prompt,
        action: selectedReply ?? action,
        missionId: mission?.id ?? null,
        missionTitle: mission?.title ?? null,
        preset: boardPreset !== "normal" ? boardPreset : null,
        modelOverride: boardModelOverride || null,
        effortOverride: boardEffortOverride || null,
      });
      showToast("ジョブを稼働ボードに投入しました");
      onClose();
    } catch (error) {
      setAgentError(error instanceof Error ? error.message : String(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-950/35 text-text backdrop-blur-sm dark:bg-black/55 dark:text-white">
      <div className="absolute inset-x-0 bottom-0 max-h-[88vh] overflow-y-auto rounded-t-md border-t border-border bg-card shadow-[0_-24px_80px_-45px_rgba(99,102,241,0.45)] dark:border-white/10 dark:bg-[#151519] dark:shadow-[0_-24px_80px_-45px_rgba(129,140,248,0.9)] md:left-1/2 md:top-1/2 md:bottom-auto md:max-h-[84vh] md:w-[720px] md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-md md:border">
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-border bg-card/95 p-5 backdrop-blur dark:border-white/10 dark:bg-[#151519]/95">
          <div>
            <p className="command-dim text-sm font-semibold">Command Sheet</p>
            <h2 className="mt-1 text-2xl font-bold">
              GO {action} / {meta.label}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-border text-muted hover:bg-hover dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
            aria-label="Command Sheetを閉じる"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 p-5">
          <div className="rounded-md border border-brand-200 bg-brand-50 p-4 dark:border-indigo-400/25 dark:bg-indigo-400/10">
            <p className="text-xs font-semibold text-brand-700 dark:text-indigo-200">Target Mission</p>
            <p className="mt-2 text-lg font-bold">{mission?.title ?? "今日のPrimary Mission案を作る"}</p>
            <p className="command-muted mt-2 text-sm">{meta.next}</p>
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            <div className="command-subcard p-3">
              <p className="command-dim text-xs">委任先</p>
              <p className="mt-1 font-semibold">{mission ? ownerLabel[mission.owner] : "たいがさん"}</p>
            </div>
            <div className="command-subcard p-3">
              <p className="command-dim text-xs">出所</p>
              <p className="mt-1 font-semibold">{mission?.source ?? "司令塔（ローカル）"}</p>
            </div>
            <div className="command-subcard p-3">
              <p className="command-dim text-xs">安全</p>
              <p className="mt-1 font-semibold text-emerald-700 dark:text-emerald-100">自動実行なし</p>
            </div>
          </div>

          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-emerald-800 dark:border-emerald-400/25 dark:bg-emerald-400/10 dark:text-emerald-50">
            <div className="flex items-start gap-2">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
              <p className="text-sm">
                このSheetは指令文の準備だけです。外部書き込み、AI自動実行、本番送信、投稿は行いません。
              </p>
            </div>
          </div>

          <div className="rounded-md border border-violet-200 bg-violet-50 p-4 dark:border-violet-400/25 dark:bg-violet-400/10">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm font-semibold text-violet-800 dark:text-violet-100">CLI Bridge</p>
                <p className="command-muted mt-1 text-sm">
                  Desktop版では、この指令を安全な相談モードでCLIへ送ります。
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {(["codex", "claude"] as AgentProvider[]).map((provider) => (
                  <Button
                    key={provider}
                    type="button"
                    variant={provider === "codex" ? "primary" : "outline"}
                    size="sm"
                    leftIcon={
                      runningAgent === provider ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Terminal className="h-3.5 w-3.5" />
                      )
                    }
                    disabled={runningAgent !== null}
                    onClick={() => void runAgent(provider)}
                  >
                    {agentLabel[provider]}へ投げる
                  </Button>
                ))}
              </div>
            </div>
            <div className="mt-3 flex flex-col gap-2 border-t border-violet-400/15 pt-3">
              <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                <p className="text-xs text-violet-700 dark:text-violet-100/80">
                  待たずに進める場合は稼働ボードへ。完了/要判断は通知でお知らせします。
                </p>
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <select
                      aria-label="ボード投入先"
                      value={boardProvider}
                      onChange={(e) => {
                        setBoardProvider(e.target.value as AgentProvider);
                        // M2: provider 切替時に override を残留させない（不正値400・非表示残留の防止）
                        setBoardModelOverride("");
                        setBoardEffortOverride("");
                      }}
                      className="rounded border border-violet-200 bg-white/70 px-1.5 py-1 text-[11px] text-violet-800 dark:border-violet-400/30 dark:bg-black/30 dark:text-zinc-200"
                    >
                      <option value="codex">{providerLabel("codex")}</option>
                      <option value="claude">{providerLabel("claude")}</option>
                    </select>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      leftIcon={<Terminal className="h-3.5 w-3.5" />}
                      disabled={runningAgent !== null}
                      onClick={() => void enqueueToBoard(boardProvider)}
                    >
                      ボードで並列実行
                    </Button>
                  </div>
                  <div className="text-text dark:text-white">
                    <PresetPicker
                      provider={boardProvider}
                      preset={boardPreset}
                      modelOverride={boardModelOverride}
                      effortOverride={boardEffortOverride}
                      onPresetChange={setBoardPreset}
                      onModelOverrideChange={setBoardModelOverride}
                      onEffortOverrideChange={setBoardEffortOverride}
                    />
                    <p className="mt-1 text-[10px] text-violet-700/80 dark:text-violet-100/60">
                      プリセットは「Codex/Claudeへ投げる」「続投」「ボード並列実行」のすべてに適用されます
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <p className="mt-3 text-xs leading-relaxed text-violet-700 dark:text-violet-100/80">
              初期版は相談専用です。ファイル編集、外部書き込み、本番送信、投稿は許可しません。
            </p>
          </div>

          {(agentResult || agentError || runningAgent) && (
            <div className="command-subcard p-4">
              <div className="flex items-center justify-between gap-3">
                <p className="command-muted text-sm font-semibold">Agent Result</p>
                {agentResult && (
                  <p className="command-faint text-xs">
                    {agentLabel[agentResult.provider]} / {Math.round(agentResult.elapsedMs / 1000)}s
                  </p>
                )}
              </div>
              {runningAgent && (
                <div className="command-muted mt-3 flex items-center gap-2 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {agentLabel[runningAgent]}が相談中...
                </div>
              )}
              {agentError && (
                <p className="mt-3 whitespace-pre-wrap break-words text-sm text-red-600 dark:text-red-200">{agentError}</p>
              )}
              {agentResult && (
                <>
                  <div className="mt-3 rounded-md border border-border bg-bg p-3 dark:border-zinc-800 dark:bg-black/40">
                    <p className="command-faint text-xs">Safety</p>
                    <p className="mt-1 text-sm text-emerald-700 dark:text-emerald-100">{agentResult.safetyMode}</p>
                  </div>
                  <pre className="command-muted mt-3 max-h-72 overflow-y-auto whitespace-pre-wrap break-words font-sans text-sm leading-relaxed">
                    {cleanedAgentOutput || "(no output)"}
                  </pre>
                  <div className="mt-3 flex flex-col gap-2 border-t border-border pt-3 dark:border-zinc-800 md:flex-row md:items-center md:justify-between">
                    <div className="command-faint min-h-5 text-xs">
                      {missionSaveState === "saved" && (
                        <span className="inline-flex items-center gap-1 text-emerald-200">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          今日のMission一覧に追加しました
                        </span>
                      )}
                      {missionSaveState === "error" && (
                        <span className="text-red-200">{missionSaveError ?? "Mission化に失敗しました"}</span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        leftIcon={<Clipboard className="h-3.5 w-3.5" />}
                        onClick={() => void copyAgentOutput()}
                      >
                        結果コピー
                      </Button>
                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        leftIcon={
                          savingMission ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Sparkles className="h-3.5 w-3.5" />
                          )
                        }
                        disabled={!canSaveMission || savingMission || blocksDuplicateMission}
                        onClick={() => void saveMissionFromResult()}
                      >
                        Missionに追加
                      </Button>
                    </div>
                  </div>
                  {cleanedAgentOutput && missionSaveState !== "saved" && (
                    <div
                      className={`mt-2 rounded-md border p-3 text-xs leading-relaxed ${
                        isWeakMissionTitlePreview
                          ? "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-50/90"
                          : "border-violet-200 bg-violet-50 text-violet-900 dark:border-violet-400/20 dark:bg-violet-400/10 dark:text-violet-50/85"
                      }`}
                    >
                      <p className="font-semibold">
                        保存前プレビュー
                      </p>
                      <p className="mt-1">
                        AI回答を「実行判断・推奨AI・投げるプロンプト・人間確認」に変換して今日タスクへ追加します。
                      </p>
                      {isWeakMissionTitlePreview && (
                        <p className="mt-2 font-semibold text-amber-800 dark:text-amber-100">
                          目的が弱すぎるため追加を止めています。もう一度Agentへ「Primary Missionのタイトルを1行で明示して」と続投してください。
                        </p>
                      )}
                      {similarMission && !isWeakMissionTitlePreview && (
                        <p className={`mt-2 font-semibold ${similarMission.exact ? "text-amber-800 dark:text-amber-100" : "text-violet-800 dark:text-violet-100"}`}>
                          {similarMission.exact
                            ? `同じMissionが既にあります: ${similarMission.title}`
                            : `似たMissionがあります: ${similarMission.title}`}
                        </p>
                      )}
                      <div className="mt-2 grid gap-2 md:grid-cols-3">
                        <div className="rounded-md border border-current/15 bg-white/50 p-2 dark:bg-black/20">
                          <p className="opacity-60">目的</p>
                          <p className="mt-1 font-semibold">{missionTitlePreview}</p>
                        </div>
                        <div className="rounded-md border border-current/15 bg-white/50 p-2 dark:bg-black/20">
                          <p className="opacity-60">GO</p>
                          <p className="mt-1 font-semibold">{executionPreview?.goLabel}</p>
                        </div>
                        <div className="rounded-md border border-current/15 bg-white/50 p-2 dark:bg-black/20">
                          <p className="opacity-60">推奨実行面</p>
                          <p className="mt-1 font-semibold">{executionPreview?.recommendedAgent}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  {agentResult.stderr && (
                    <div className="mt-3 rounded-md border border-border bg-bg dark:border-zinc-800 dark:bg-black/30">
                      <button
                        type="button"
                        className="command-dim flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-xs font-semibold hover:text-text dark:hover:text-zinc-200"
                        onClick={() => setShowDiagnostics((value) => !value)}
                      >
                        Diagnostics
                        <ChevronDown className={`h-3.5 w-3.5 transition ${showDiagnostics ? "rotate-180" : ""}`} />
                      </button>
                      {showDiagnostics && (
                        <pre className="max-h-32 overflow-y-auto whitespace-pre-wrap break-words border-t border-border px-3 py-2 font-mono text-xs leading-relaxed text-amber-700 dark:border-zinc-800 dark:text-amber-200">
                          {agentResult.stderr}
                        </pre>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div className="rounded-md border border-sky-200 bg-sky-50 p-4 dark:border-sky-400/25 dark:bg-sky-400/10">
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-2">
              <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-sky-700 dark:text-sky-100" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-sky-800 dark:text-sky-100">Flow Reply</p>
                <p className="mt-1 text-xs leading-relaxed text-sky-700 dark:text-sky-100/80">
                  A/B/C/Dだけで足りない時は、条件や違和感をそのまま書いて続投できます。
                </p>
              </div>
              </div>
              {hasReply && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedReply(null);
                    setReplyDraft("");
                  }}
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-sky-200 text-sky-700 hover:bg-sky-100 dark:border-sky-200/20 dark:text-sky-100 dark:hover:bg-sky-200/10"
                  aria-label="返答をクリア"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <div className="mt-3 grid grid-cols-4 gap-2">
              {(Object.keys(actionMeta) as CommandAction[]).map((replyAction) => (
                <button
                  key={replyAction}
                  type="button"
                  onClick={() => setSelectedReply(replyAction)}
                  className={`rounded-md border px-3 py-2 text-sm font-semibold transition ${
                    selectedReply === replyAction
                      ? "border-sky-300 bg-sky-200 text-sky-950 dark:border-sky-200 dark:bg-sky-200 dark:text-zinc-950"
                      : "border-sky-200 bg-white/50 text-sky-800 hover:bg-sky-100 dark:border-sky-200/25 dark:bg-black/20 dark:text-sky-50 dark:hover:bg-sky-200/10"
                  }`}
                >
                  <span className="block text-base">{replyAction}</span>
                  <span className="mt-0.5 block text-[10px] font-medium opacity-75">{actionMeta[replyAction].label}</span>
                </button>
              ))}
            </div>

            <textarea
              value={replyDraft}
              onChange={(event) => setReplyDraft(event.target.value)}
              rows={4}
              className="mt-3 w-full resize-none rounded-md border border-sky-200 bg-white px-3 py-2 text-sm leading-relaxed text-text outline-none placeholder:text-dim focus:border-sky-400 dark:border-sky-200/25 dark:bg-black/30 dark:text-zinc-100 dark:placeholder:text-zinc-500 dark:focus:border-sky-200"
              placeholder="例: B寄りだけど、診断LPと予約URLの役割が混ざらない案にして。dry-runまでで本番送信は絶対にしない。"
            />
            {hasReply && (
              <div className="mt-2 rounded-md border border-sky-200 bg-white/60 px-3 py-2 text-xs leading-relaxed text-sky-800 dark:border-sky-200/15 dark:bg-black/20 dark:text-sky-50/80">
                {selectedReply && <span className="font-semibold text-sky-800 dark:text-sky-100">GO {selectedReply}: </span>}
                {replyDraft.trim() || actionMeta[selectedReply ?? action].next}
              </div>
            )}

            <div className="mt-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <p className="text-xs text-sky-700 dark:text-sky-100/75">
                続投は相談モードのみ。ファイル編集・外部書き込み・本番送信は許可しません。
              </p>
              <div className="grid grid-cols-2 gap-2">
                {(["codex", "claude"] as AgentProvider[]).map((provider) => (
                  <Button
                    key={provider}
                    type="button"
                    variant={provider === "codex" ? "primary" : "outline"}
                    size="sm"
                    leftIcon={
                      runningAgent === provider ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Terminal className="h-3.5 w-3.5" />
                      )
                    }
                    disabled={runningAgent !== null || !hasReply}
                    onClick={() => void runAgent(provider, "reply")}
                  >
                    {agentLabel[provider]}へ続投
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <div className="command-subcard p-4">
            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                className="command-muted flex min-w-0 items-center gap-2 text-left text-sm font-semibold hover:text-text dark:hover:text-zinc-100"
                onClick={() => setShowGeneratedCommand((value) => !value)}
              >
                <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition ${showGeneratedCommand ? "rotate-180" : ""}`} />
                Generated Command
                <span className="command-faint truncate text-xs font-normal">
                  {hasReply ? "続投用プロンプト" : "初回プロンプト"}
                </span>
              </button>
              <div className="shrink-0">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  leftIcon={<Clipboard className="h-3.5 w-3.5" />}
                  onClick={() => void copyPrompt()}
                >
                  コピー
                </Button>
              </div>
            </div>
            {showGeneratedCommand && (
              <pre className="command-muted mt-3 max-h-56 overflow-y-auto whitespace-pre-wrap break-words rounded-md border border-border bg-bg p-3 font-mono text-xs leading-relaxed dark:border-zinc-800 dark:bg-black/30">
                {replyPrompt}
              </pre>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
